import { cloneDeep, isEqual } from 'lodash';

/**
 * Checks if two values are considered equal when treating falsy values as equal
 * Treats the following as equal:
 * - null === undefined
 * - empty string === undefined
 * - empty string === null
 * - false === empty string
 * - false === null
 * - false === undefined
 * @param val1 First value to compare
 * @param val2 Second value to compare
 * @returns boolean indicating if the values are falsy-equal
 */
function areFalsyEqual(val1: any, val2: any): boolean {
  const isFalsy1 = isFalsyValue(val1);
  const isFalsy2 = isFalsyValue(val2);

  return isFalsy1 && isFalsy2;
}

/**
 * Performs a deep equality comparison between two values
 * @param obj1 First value to compare
 * @param obj2 Second value to compare
 * @returns boolean indicating if the values are deeply equal
 */
export function deepEqual(obj1: DeepEqualType, obj2: DeepEqualType): boolean {
  return isEqual(obj1, obj2);
}

/**
 * Checks if a value is considered falsy (null, undefined, empty string, or false)
 * @param val Value to check
 * @returns boolean indicating if the value is falsy
 */
function isFalsyValue(val: any): boolean {
  return val === null || val === undefined || val === '' || val === false;
}

/**
 * Performs a deep equality comparison between two values, treating falsy values as equal
 * @param obj1 First value to compare
 * @param obj2 Second value to compare
 * @returns boolean indicating if the values are deeply equal (with falsy values treated as equal)
 */
function deepEqualWithFalsyEquality(
  obj1: DeepEqualType,
  obj2: DeepEqualType,
): boolean {
  // Check if both values are falsy-equal (null, undefined, empty string, false)
  if (areFalsyEqual(obj1, obj2)) {
    return true;
  }

  // For Date objects, compare their time values
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // If types don't match, they're not equal
  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqualWithFalsyEquality(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  // Handle objects
  if (
    typeof obj1 === 'object' &&
    typeof obj2 === 'object' &&
    obj1 !== null &&
    obj2 !== null
  ) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key)) {
        return false;
      }
      if (!deepEqualWithFalsyEquality((obj1 as any)[key], (obj2 as any)[key])) {
        return false;
      }
    }

    return true;
  }

  // For primitive values, use strict equality
  return obj1 === obj2;
}

export function deepClone<T>(obj: T): T {
  return cloneDeep(obj);
}

/**
 * Recursively removes specified fields from an object or array
 * @param obj Object or array to process
 * @param fieldsToIgnore Fields to remove
 * @returns Processed object with specified fields removed
 */
function removeFieldsRecursively<T>(obj: T, fieldsToIgnore: string[]): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      removeFieldsRecursively(item, fieldsToIgnore),
    ) as unknown as T;
  }

  // Handle objects
  const result = { ...obj };
  fieldsToIgnore.forEach((field) => {
    delete (result as any)[field];
  });

  // Process nested objects
  Object.keys(result).forEach((key) => {
    const value = (result as any)[key];
    if (value && typeof value === 'object') {
      (result as any)[key] = removeFieldsRecursively(value, fieldsToIgnore);
    }
  });

  return result;
}

/**
 * Compares two objects for equality while ignoring specified fields
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @param fieldsToIgnore Array of field names to ignore during comparison
 * @param treatFalsyAsEqual If true, treats falsy values (null, undefined, empty string, false) as equal (default: false)
 * @returns boolean indicating if the objects are equal (ignoring specified fields)
 */
export function compareIgnoringFields<T>(
  obj1: T,
  obj2: T,
  fieldsToIgnore: string[],
  treatFalsyAsEqual: boolean = false,
): boolean {
  if (!obj1 || !obj2) {
    if (!treatFalsyAsEqual) {
      return obj1 === obj2;
    } else {
      return areFalsyEqual(obj1, obj2);
    }
  }

  // Create deep copies of both objects
  const obj1Copy = deepClone(obj1);
  const obj2Copy = deepClone(obj2);

  // Remove the specified fields from both objects recursively
  const filteredObj1 = removeFieldsRecursively(obj1Copy, fieldsToIgnore);
  const filteredObj2 = removeFieldsRecursively(obj2Copy, fieldsToIgnore);

  // Compare the filtered objects using the appropriate deepEqual function
  if (treatFalsyAsEqual) {
    return deepEqualWithFalsyEquality(
      filteredObj1 as DeepEqualType,
      filteredObj2 as DeepEqualType,
    );
  } else {
    return deepEqual(filteredObj1, filteredObj2);
  }
}

export type DeepEqualType =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | Array<any>
  | { [key: string]: any }
  | object;

/**
 * Generates a unique ID for an object based on its properties
 * Sanitizes the ID to ensure it's valid for HTML id attributes
 * @param object - The object to generate an ID for
 * @returns A unique string ID safe for HTML
 */
export function getObjectId(object: any): string {
  let baseId =
    object.id || object.name || Math.random().toString(36).substring(2, 11);

  // Sanitize the ID: remove/replace invalid characters for HTML id attribute
  // Valid characters: letters, digits, hyphens, underscores
  // Replace invalid chars with underscore
  return baseId
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9\-_]+/g, '_');
}
